/**
 * QuickAssignModal — shortcut to assign a job's top-recommended contractor.
 *
 * Push-Mode aware: on assign the modal stays open and renders an inline
 * `PushNextStepPanel` so the operator chains into the next action.
 */
import { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, Star, ArrowRight, Trophy, ArrowLeft, Info, FileText, ShoppingCart, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { getDispatchRecommendations, type ReadinessGapKind } from "@/data/dispatchRecommendation";
import type { Job, Issue } from "@/data/types";
import { cn } from "@/lib/utils";
import { PushProgressTracker } from "@/components/PushProgressTracker";
import { stageKeyToPushStep, type TrackerStageKey } from "@/lib/pushStageNavigation";
import { PushNextStepPanel, type PushNextStep } from "@/components/quick-actions/PushNextStepPanel";
import { PushContextHeader } from "@/components/quick-actions/PushContextHeader";
import { buildPushHandoff } from "@/data/pushHandoffEngine";
import { derivePreSpendGate, getGatedCtaLabel } from "@/data/preSpendGate";
import { PreSpendGateBanner } from "@/components/PreSpendGateBanner";
import { assertContractorAssignable } from "@/data/payProfileGuard";
import { assertJobKitReady } from "@/data/kitDispatchGuard";
import {
  canMarkJobReady,
  isExecutionReady,
  EXECUTION_LOCK_BLOCK_TITLE,
  getExecutionLockBlockDescription,
  getExecutionLockToastOptions,
} from "@/data/executionReadyLock";
import { getKit } from "@/data/materialKit";
import {
  assertMaterialAlignment,
  describeAlignmentBlock,
} from "@/data/materialAlignmentGate";
import { useVendorPO } from "@/data/VendorPOContext";
import { useOrderDraft } from "@/data/OrderDraftContext";
import { JobService } from "@/services/job.service";

/** Map a readiness gap → the PushNextStep that resolves it (chains inside Push Mode). */
const GAP_TO_STEP: Record<ReadinessGapKind, PushNextStep> = {
  permit:    { kind: "view_permit" },
  materials: { kind: "generate_po" },
  kit:       { kind: "open_job" },
  issue:     { kind: "resolve_issue" },
};
const GAP_ICON: Record<ReadinessGapKind, React.ElementType> = {
  permit: FileText, materials: ShoppingCart, kit: CheckCircle2, issue: AlertCircle,
};

export interface QuickAssignModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  job: Job | null;
  openIssues?: Issue[];
  /** Fired after a successful assignment with the projected updated job. */
  onSuccess?: (updatedJob: Job) => void;
  /** Push-Mode chain: render a "Back" button. Hidden once they've picked a contractor. */
  onBack?: () => void;
  /** Push-Mode chain: operator picked a next step from the success panel. */
  onChooseNextStep?: (step: PushNextStep, updatedJob: Job) => void;
}

export function QuickAssignModal({ open, onOpenChange, job, openIssues = [], onSuccess, onBack, onChooseNextStep }: QuickAssignModalProps) {
  const { contractors, setJobs, jobs, issues, credits, setFollowUpTasks, payProfiles, materialKits, buildReviewEvents } = useAppData();
  const { purchaseOrders } = useVendorPO();
  const { drafts } = useOrderDraft();
  const { roleInfo } = useRole();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [sent, setSent] = useState(false);
  const [updatedJob, setUpdatedJob] = useState<Job | null>(null);
  /** Snapshot of the top gate at assign time — drives the post-assign confirmation summary. */
  const [lockedGateLabel, setLockedGateLabel] = useState<string | null>(null);
  /** Phase 4c: remember the gate that was active when the modal opened, so we
   *  can flash a "✓ cleared" notice if it resolves while still open. */
  const [initialGateLabel, setInitialGateLabel] = useState<string | null>(null);
  const [showClearedFlash, setShowClearedFlash] = useState(false);

  const result = useMemo(() => {
    if (!job) return null;
    return getDispatchRecommendations(job, contractors ?? [], [], jobs ?? [], issues ?? [], credits ?? [], [], materialKits ?? []);
  }, [job, contractors, jobs, issues, credits, materialKits]);

  useEffect(() => {
    if (open && result?.topPick) {
      setSelectedId(result.topPick.contractor.id);
      setDirty(false);
      setSent(false);
      setUpdatedJob(null);
      setLockedGateLabel(null);
      setInitialGateLabel(result.gated && result.gateBlockers[0] ? result.gateBlockers[0].label.toLowerCase() : null);
      setShowClearedFlash(false);
    }
  }, [open, result?.topPick?.contractor.id]);

  // Phase 4c: live gate recompute — if the modal is open, no assignment yet,
  // and the previously-active gate has cleared, surface an inline notice.
  // Never auto-advance: operator stays in control.
  useEffect(() => {
    if (!open || sent || !initialGateLabel) return;
    if (!result?.gated) {
      setShowClearedFlash(true);
    }
  }, [open, sent, initialGateLabel, result?.gated]);

  // Pre-spend gate — crew assignment commits company labor + obligation, blocked until clear.
  // Must be computed BEFORE any early return to keep hook order stable.
  const preSpendGate = useMemo(() => (job ? derivePreSpendGate(job) : null), [job]);

  if (!job || !preSpendGate) return null;

  const top3 = result?.recommendations.slice(0, 3) ?? [];

  const handleConfirm = () => {
    if (!preSpendGate.cleared) {
      toast.error("Crew assignment blocked", { description: preSpendGate.shortReason });
      return;
    }
    if (!selectedId) {
      toast.error("Select a contractor first");
      return;
    }
    const c = contractors.find((x) => x.id === selectedId);
    if (!c) return;
    // ── Execution Ready lock (checked FIRST) ──
    if (!isExecutionReady(job)) {
      const readinessVerdict = canMarkJobReady({
        job,
        kit: getKit(materialKits, job.id),
        payProfiles,
        contractors,
        buildReviewEvents,
      });
      toast.warning(
        EXECUTION_LOCK_BLOCK_TITLE,
        getExecutionLockToastOptions(getExecutionLockBlockDescription(readinessVerdict)),
      );
    }
    // ── Material Alignment gate (spec === po === kit) ──
    const alignment = assertMaterialAlignment(job, {
      kit: getKit(materialKits, job.id),
      orderDrafts: drafts,
      purchaseOrders,
    });
    if (!alignment.ok) {
      const copy = describeAlignmentBlock(alignment);
      toast.error(copy.title, { description: copy.description });
      return;
    }
    // ── Pay Profile guard ──
    const guard = assertContractorAssignable({
      contractor: c, projectType: job.projectType, payProfiles,
    });
    if (!guard.allowed) {
      toast.error(guard.blockTitle, { description: guard.blockDescription });
      return;
    }
    // ── Material Kit dispatch guard ──
    const kitGuard = assertJobKitReady({ kit: getKit(materialKits, job.id) });
    if (!kitGuard.allowed) {
      toast.warning("Assigned with readiness warning", { description: kitGuard.blockDescription });
    }
    let next: Job | null = null;
    let previous: Job | null = null;
    setJobs((prev) =>
      prev.map((j) => {
        if (j.id !== job.id) return j;
        previous = j;
        const updated: Job = {
          ...j,
          assignmentType: "Subcontractor" as const,
          crewId: undefined,
          assignedSubcontractor: c.companyName,
          dispatchStatus: "Awaiting Response" as const,
          activityHistory: [
            ...(j.activityHistory ?? []),
            {
              id: `act-${Date.now()}`,
              date: new Date().toISOString().slice(0, 10),
              user: "User",
              action: `Quick-assigned ${c.companyName} via top recommendation`,
              noteType: "system" as const,
            },
          ],
        };
        next = updated;
        return updated;
      }),
    );
    if (next) {
      void JobService.update(next.id, {
        assignmentType: next.assignmentType,
        crewId: undefined,
        assignedSubcontractor: next.assignedSubcontractor,
        dispatchStatus: next.dispatchStatus,
        activityHistory: next.activityHistory,
      }).then(({ data, error }) => {
        if (error || !data) {
          if (previous) {
            setJobs((prev) => prev.map((j) => (j.id === previous!.id ? previous! : j)));
          }
          toast.error("Assign crew failed to persist", {
            description: "Changes were reverted. Please retry.",
          });
          return;
        }
        setJobs((prev) => prev.map((j) => (j.id === data.id ? data : j)));
        setUpdatedJob(data);
        if (onSuccess) onSuccess(data);
      }).catch((err) => {
        console.error("Failed to persist crew assignment:", err);
        if (previous) {
          setJobs((prev) => prev.map((j) => (j.id === previous!.id ? previous! : j)));
        }
        toast.error("Assign crew failed to persist", {
          description: "Changes were reverted. Please retry.",
        });
      });
    }
    toast.success(`Assigned ${c.companyName} — pending acceptance`);
    if (next) {
      setUpdatedJob(next);
      setSent(true);
      // Capture the top gate at assign time so the success summary names what's still locking dispatch.
      if (result?.gated && result.gateBlockers[0]) {
        setLockedGateLabel(result.gateBlockers[0].label.toLowerCase());
      }
    }
  };

  const handleHandOffConfirmed = (
    assignee: string,
    dueIso: string,
    selection?: import("./PushHandoffPrompt").PushHandoffSelection,
  ) => {
    if (!updatedJob) { onOpenChange(false); return; }
    const { task, activityEntry } = buildPushHandoff({
      job: updatedJob,
      context: { reasonFragment: "after crew assignment", sourceLabel: "Quick Assign" },
      assignee,
      assigneeRoleKey: selection?.roleKey ?? null,
      assigneeUserId: selection?.userId ?? null,
      dueDate: dueIso,
      initiatedBy: roleInfo.label,
    });
    setFollowUpTasks((prev) => [...prev, task]);
    setJobs((prev) =>
      prev.map((j) =>
        j.id !== updatedJob.id ? j : { ...j, activityHistory: [...(j.activityHistory ?? []), activityEntry] },
      ),
    );
    toast.success(`Handed off to ${assignee} — due ${dueIso.slice(0, 10)}`);
    onOpenChange(false);
  };

  const assignedName = updatedJob?.assignedSubcontractor;

  // Chain integrity: when chained-into (onBack present) and operator dismisses
  // via X / outside-click without picking a contractor, return to prior modal
  // instead of closing the entire chain. Explicit Cancel still exits cleanly.
  const handleDismiss = (next: boolean) => {
    if (next) { onOpenChange(true); return; }
    if (onBack && !dirty && !sent) { onBack(); return; }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent className="w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" /> Quick Assign Contractor
          </DialogTitle>
          <DialogDescription>
            {job.customerName} — {job.id}. Top matches by dispatch fit. For full comparison, use the dispatch view.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <PushContextHeader job={updatedJob ?? job} openIssues={openIssues} />
          <PushProgressTracker
            job={updatedJob ?? job}
            activeKey={sent ? undefined : "crew"}
            onStageClick={(key) => {
              if (key === "crew") return;
              const target = updatedJob ?? job;
              onChooseNextStep?.(stageKeyToPushStep(key as TrackerStageKey, target.id), target);
            }}
          />

          {sent && updatedJob ? (
            <PushNextStepPanel
              job={updatedJob}
              openIssues={openIssues}
              headline="Crew assigned — what's next?"
              summary={
                lockedGateLabel
                  ? `${assignedName ?? "Crew"} · pending acceptance. Dispatch still gated — clear ${lockedGateLabel} to proceed.`
                  : assignedName ? `${assignedName} · pending acceptance` : undefined
              }
              autoArm={!lockedGateLabel}
              onContinue={(step) => {
                if (onChooseNextStep) onChooseNextStep(step, updatedJob);
                else onOpenChange(false);
              }}
              onPause={() => onOpenChange(false)}
              onHandOffConfirmed={handleHandOffConfirmed}
              handoffReasonFragment="after crew assignment"
            />
          ) : (
            <>
              {/* Pre-spend gate — must clear BEFORE any operational gate (permit/materials).
                  This is a contractual/financial gate, not a readiness gate. */}
              {!preSpendGate.cleared && (
                <PreSpendGateBanner
                  gate={preSpendGate}
                  context="Crew assignment is blocked — assignment obligates company labor and travel."
                />
              )}

              {/* Phase 4c: gate cleared while modal was open — informational, no auto-advance. */}
              {showClearedFlash && !result?.gated && initialGateLabel && (
                <div className="rounded-md border border-success/30 bg-success/5 p-2 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
                  <p className="text-[12px] font-semibold text-foreground">
                    {initialGateLabel.charAt(0).toUpperCase() + initialGateLabel.slice(1)} cleared ✓ — dispatch now available
                  </p>
                </div>
              )}
              {result?.gated && (
                <div className="rounded-md border border-warning/30 bg-warning/5 p-3">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-warning mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[12px] font-semibold text-foreground">
                        {result.gateBlockers[0]
                          ? `Dispatch gated — clear ${result.gateBlockers[0].label.toLowerCase()} to unlock`
                          : "Dispatch gated — assignment still available"}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Assignment is allowed now. Dispatch unlocks once readiness is cleared.
                      </p>
                      {result.gateBlockers.length > 0 && onChooseNextStep && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {result.gateBlockers.slice(0, 3).map((gap, idx) => {
                            const Icon = GAP_ICON[gap.kind];
                            const isPrimary = idx === 0;
                            return (
                              <button
                                key={gap.kind}
                                type="button"
                                onClick={() => onChooseNextStep(GAP_TO_STEP[gap.kind], updatedJob ?? job)}
                                className={cn(
                                  "inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition-colors",
                                  isPrimary
                                    ? "border-warning bg-warning text-warning-foreground hover:bg-warning/90 font-semibold shadow-sm"
                                    : "border-warning/40 bg-background hover:bg-warning/10 text-foreground",
                                )}
                                title={gap.detail}
                              >
                                <Icon className={cn("h-3 w-3", isPrimary ? "text-warning-foreground" : "text-warning")} />
                                {gap.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Terminal-state notice (e.g. job already completed). Phrased as informational —
                  "blocked" is reserved for internal types and never shown to operators. */}
              {result?.blocked && result.blockReason && (
                <div className="rounded-md border border-muted bg-muted/30 p-2 text-[11px] text-muted-foreground">
                  {result.blockReason}
                </div>
              )}

              {top3.length === 0 ? (
                <div className="text-sm text-muted-foreground text-center py-6">
                  No eligible contractors found.
                </div>
              ) : (
                top3.map((rec, idx) => {
                  const isSelected = selectedId === rec.contractor.id;
                  return (
                    <button
                      key={rec.contractor.id}
                      type="button"
                      onClick={() => { setSelectedId(rec.contractor.id); setDirty(true); }}
                      className={cn(
                        "w-full text-left rounded-md border-2 p-3 transition-all",
                        isSelected
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50",
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {idx === 0 && <Trophy className="h-3.5 w-3.5 text-warning" />}
                            <span className="font-semibold text-sm">{rec.contractor.companyName}</span>
                            <Badge variant="outline" className="text-[10px]">{rec.reliabilityTier}</Badge>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {rec.contractor.tradeType} · {rec.contractor.serviceArea}
                          </p>
                          {rec.riskFlags.length > 0 && (
                            <div className="flex gap-1 mt-1.5 flex-wrap">
                              {rec.riskFlags.slice(0, 2).map((f) => (
                                <Badge key={f} variant="destructive" className="text-[10px] py-0 px-1.5">{f}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="flex items-center gap-1 text-sm font-bold">
                            <Star className="h-3.5 w-3.5 fill-amber-400 text-warning" />
                            {rec.score}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-0.5">fit score</p>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}

              <Link
                to="/scheduling-dispatch"
                className="text-xs text-primary hover:underline inline-flex items-center gap-1 pt-1"
              >
                Open full dispatch view <ArrowRight className="h-3 w-3" />
              </Link>
            </>
          )}
        </div>

        {!sent && (
          <DialogFooter className="gap-2 sm:gap-2">
            {onBack && !dirty && (
              <Button variant="ghost" size="sm" onClick={onBack} className="mr-auto gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedId || top3.length === 0 || !preSpendGate.cleared}
              title={!preSpendGate.cleared ? preSpendGate.shortReason : undefined}
            >
              {!preSpendGate.cleared
                ? getGatedCtaLabel("Assign", preSpendGate)
                : result?.gated
                  ? `Assign · dispatch gated`
                  : selectedId
                    ? `Assign ${contractors.find((c) => c.id === selectedId)?.companyName ?? ""} (ready)`.trim()
                    : "Assign"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
